// Helper function to decode HTML entities
function decodeHtmlEntities(text) {
  if (!text) return '';

  // Handle double-encoded entities
  let decoded = text;

  // First pass: replace &amp; with & to handle double-encoding
  decoded = decoded.replace(/&amp;/g, '&');

  // Second pass: use textarea trick to decode all entities
  const textArea = document.createElement('textarea');
  textArea.innerHTML = decoded;
  decoded = textArea.value;

  // Handle special case for &amp;amp; (double-encoded &)
  decoded = decoded.replace(/&amp;amp;/g, '&amp;');

  // Direct fix for the specific format shown in the example
  if (decoded.includes('&lt;p&gt;')) {
    // This is a case where HTML tags are encoded as entities
    decoded = decoded.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  }

  return decoded;
}

const PriceDisplay = {
  // Track processed widgets
  processedWidgets: new Set(),

  init: function () {
    console.log('init price display');

    // Process any existing widgets first
    this.processExistingWidgets();

    // Create a MutationObserver to watch for widget content updates
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check if the widget container got its content
        if (mutation.target.matches('[v65remotejs="addToCartForm"]')) {
          const widget = mutation.target;
          // Only process if it has price wrapper and hasn't been processed
          if (widget.querySelector('.v65-widgetProduct-addToCart-priceWrapper') && !this.processedWidgets.has(widget)) {
            console.log('New widget content loaded:', widget);
            this.processWidget(widget);
          }
        }
      });
    });

    // Start observing with the configured parameters
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Add login detection on window load
    window.addEventListener('load', () => {
      this.detectLoginStatus();
    });
  },

  processExistingWidgets: function () {
    const widgets = document.querySelectorAll('[v65remotejs="addToCartForm"]');
    console.log('Found existing widgets:', widgets.length);

    widgets.forEach((widget) => {
      if (widget.querySelector('.v65-widgetProduct-addToCart-priceWrapper') && !this.processedWidgets.has(widget)) {
        console.log('Processing existing widget');
        this.processWidget(widget);
      }
    });
  },

  processWidget: async function (widget) {
    // Skip widgets in sticky nav
    if (widget.closest('.js-winesStickyNav')) {
      console.log('Skipping sticky nav widget');
      return;
    }

    // Mark this widget as processed
    this.processedWidgets.add(widget);

    // Get the product ID from the closest container
    const productContainer = widget.closest('[data-product-id]');
    if (!productContainer) {
      console.log('No product container found');
      return;
    }

    const productId = productContainer.dataset.productId;
    console.log('Processing widget:', { productId, widget });

    try {
      // Fetch pricing settings from our API
      const response = await fetch(`/wp-json/prv/v1/product-pricing/${productId}`);
      const settings = await response.json();

      // Check if variant should be hidden
      if (settings.hideVariant === 'hide') {
        console.log('Hiding variant:', productId);
        // Hide the entire product container
        if (productContainer) {
          productContainer.style.display = 'none';
        }
        return;
      }

      // Handle product status
      if (settings.status) {
        this.handleProductStatus(widget, settings);
      }

      // Find price wrapper in this widget
      const priceWrapper = widget.querySelector('.v65-widgetProduct-addToCart-priceWrapper');
      if (!priceWrapper) return;

      // Apply price modifications based on settings
      this.updatePriceDisplay(priceWrapper, settings);
    } catch (error) {
      console.error('Error processing widget:', error);
      // Remove from processed set if there was an error
      this.processedWidgets.delete(widget);
    }
  },

  updatePriceDisplay: function (wrapper, settings) {
    // If excluded from sale, leave default WinesDirect display
    if (settings.displayType === 'excluded') return;

    const retailPrice = wrapper.querySelector('.v65-widgetProduct-addToCart-price');
    const memberPrice = wrapper.querySelector('.v65-widgetProduct-addToCart-memberPrice');

    if (!retailPrice || !memberPrice) return;

    // Get the rates based on display type
    const rates = settings.rates;

    // Apply discount display - pass the elements directly
    this.applyDiscountDisplay(retailPrice, memberPrice, rates);
  },

  applyDiscountDisplay: function (retailPriceEl, memberPriceEl, rates) {
    if (!retailPriceEl || !memberPriceEl) return;

    // Extract original prices
    const originalRetailPrice = this.extractPrice(retailPriceEl.textContent);
    const originalMemberPrice = this.extractPrice(memberPriceEl.textContent);

    // Extract the size text from the existing element
    const sizeText = retailPriceEl.querySelector('.size-text')?.textContent || '';

    if (originalRetailPrice && originalMemberPrice) {
      // Calculate discounted prices
      const discountedRetailPrice = originalRetailPrice * (1 - rates.retail / 100);
      const discountedMemberPrice = originalRetailPrice * (1 - rates.member / 100);

      // Format the prices
      const formattedRetailPrice = this.formatPrice(discountedRetailPrice);
      const formattedMemberPrice = this.formatPrice(discountedMemberPrice);

      // Update the display
      retailPriceEl.innerHTML = `
        <span class="original-price">$${originalRetailPrice.toFixed(2)}</span>
        ${formattedRetailPrice} <span class="retail-member-text">*Retail Price</span>
        <span class="size-text">${sizeText}</span>
      `;

      memberPriceEl.innerHTML = `
        <span class="original-price">$${originalMemberPrice.toFixed(2)}</span>
        ${formattedMemberPrice} <span class="retail-member-text">*Member Price</span>
        <span class="size-text">${sizeText}</span>
      `;

      // Add debug log to confirm price update
      console.log('Prices updated:', {
        retail: { original: originalRetailPrice, discounted: discountedRetailPrice },
        member: { original: originalMemberPrice, discounted: discountedMemberPrice },
        size: sizeText,
      });
    }
  },

  formatPrice: function (price) {
    return `$${price.toFixed(2)}`;
  },

  extractPrice: function (priceString) {
    const match = priceString.match(/\$(\d+(\.\d{2})?)/);
    return match ? parseFloat(match[1]) : null;
  },

  handleProductStatus: function (widget, settings) {
    const status = settings.status;
    const showClubStatusMessage = settings.showClubStatusMessage || false;
    const customStatusMessage = settings.customStatusMessage || '';
    const priceWrapper = widget.querySelector('.v65-widgetProduct-addToCart-priceWrapper');
    const addToCartButton = widget.querySelector('.v65-widgetProduct-addToCart-button');
    const quantitySelector = widget.querySelector('.v65-widgetProduct-addToCart-quantity');

    // Get the product container for adding classes
    const productContainer = widget.closest('[data-product-id]');
    if (!productContainer) return;

    // Add status class to the product container
    productContainer.classList.add(`product-status-${status}`);

    // If there's a custom message and it should be shown, add a class to indicate it
    if (customStatusMessage && showClubStatusMessage) {
      productContainer.classList.add('has-custom-message');

      // For 'forsale' products with custom messages, add a specific class
      if (status === 'forsale') {
        productContainer.classList.add('has-forsale-message');
      }
    } else {
      // Make sure to remove the class if it was previously added
      productContainer.classList.remove('has-custom-message');
      productContainer.classList.remove('has-forsale-message');
    }

    // Remove any existing status messages
    const existingStatus = widget.querySelector('.product-status-message');
    if (existingStatus) {
      existingStatus.remove();
    }

    // Remove any existing custom messages
    const existingCustomMessage = widget.querySelector('.club-only-message');
    if (existingCustomMessage) {
      existingCustomMessage.remove();
    }

    // Create status message element
    const statusElement = document.createElement('div');
    statusElement.classList.add('product-status-message');

    if (status === 'soldout') {
      // Handle sold out products
      if (addToCartButton) {
        addToCartButton.style.display = 'none';
      }

      // Hide quantity selector for sold out products
      if (quantitySelector) {
        quantitySelector.style.display = 'none';
      }

      statusElement.classList.add('status-soldout');
      statusElement.textContent = customStatusMessage || 'Sold Out';

      // Only add the status message on single product pages
      // The shop page will use the ::after pseudo-element
      if (document.body.classList.contains('single-wine')) {
        if (priceWrapper) {
          priceWrapper.after(statusElement);
        }
      }
    } else if (status === 'clubonly') {
      // Handle club only products
      const isLoggedIn = document.body.classList.contains('v65logged-in') || document.querySelector('.v65-widgetLogin-editProfile') !== null;

      // Add custom message as data attribute to price wrapper
      if (priceWrapper) {
        priceWrapper.setAttribute('data-custom-message', customStatusMessage || '');
        priceWrapper.setAttribute('data-club-message', 'Club Only');
      }

      if (!isLoggedIn) {
        // User is not logged in
        if (addToCartButton) {
          addToCartButton.style.display = 'none';
        }

        // Hide quantity selector for non-members on club-only products
        if (quantitySelector) {
          quantitySelector.style.display = 'none';
        }

        // Create a custom message element if one exists and should be shown
        if (customStatusMessage && showClubStatusMessage) {
          const messageElement = document.createElement('p');
          messageElement.classList.add('club-only-message');

          try {
            // Try to decode HTML entities
            messageElement.innerHTML = decodeHtmlEntities(customStatusMessage);

            // Check if the content still contains encoded HTML
            if (messageElement.textContent.includes('&lt;') || messageElement.textContent.includes('&gt;')) {
              // Fallback: create a new element with the raw text
              messageElement.textContent = 'Club Members – ';

              // Create a login link
              const loginLink = document.createElement('a');
              loginLink.href = '/index.cfm?method=memberLogin.showLogin&referrerQueryString=';
              loginLink.textContent = 'Log In';

              messageElement.appendChild(loginLink);
              messageElement.appendChild(document.createTextNode(' to See Your Savings at Checkout'));
            }
          } catch (error) {
            console.error('Error setting HTML content:', error);
            // Fallback to plain text
            messageElement.textContent = 'Club Members – Log In to See Your Savings at Checkout';
          }

          if (priceWrapper) {
            priceWrapper.appendChild(messageElement);
          }
        }

        // Only add the status message on single product pages
        // The shop page will use the ::after pseudo-element
        if (document.body.classList.contains('single-wine')) {
          statusElement.classList.add('status-clubonly');
          statusElement.textContent = 'Club Only';

          // Add custom message if available and should be shown
          if (customStatusMessage && showClubStatusMessage) {
            const customMessageElement = document.createElement('p');
            customMessageElement.classList.add('club-only-custom-message');

            try {
              // Try to decode HTML entities
              customMessageElement.innerHTML = decodeHtmlEntities(customStatusMessage);

              // Check if the content still contains encoded HTML
              if (customMessageElement.textContent.includes('&lt;') || customMessageElement.textContent.includes('&gt;')) {
                // Fallback: create a new element with the raw text
                customMessageElement.textContent = 'Club Members – ';

                // Create a login link
                const loginLink = document.createElement('a');
                loginLink.href = '/index.cfm?method=memberLogin.showLogin&referrerQueryString=';
                loginLink.textContent = 'Log In';

                customMessageElement.appendChild(loginLink);
                customMessageElement.appendChild(document.createTextNode(' to See Your Savings at Checkout'));
              }
            } catch (error) {
              console.error('Error setting HTML content:', error);
              // Fallback to plain text
              customMessageElement.textContent = 'Club Members – Log In to See Your Savings at Checkout';
            }

            statusElement.appendChild(document.createElement('br'));
            statusElement.appendChild(customMessageElement);
          }

          // Add join club link
          const joinClubLink = document.createElement('a');
          joinClubLink.href = '/wine-club/';
          joinClubLink.classList.add('join-club-button');
          joinClubLink.textContent = 'Join Our Wine Club';

          statusElement.appendChild(document.createElement('br'));
          statusElement.appendChild(joinClubLink);

          if (priceWrapper) {
            priceWrapper.after(statusElement);
          }
        }
      } else {
        // User is logged in, ensure add to cart and quantity are visible
        if (addToCartButton) {
          addToCartButton.style.display = 'block';
        }

        if (quantitySelector) {
          quantitySelector.style.display = 'block';
        }

        // Show a success message for club members
        if (customStatusMessage && showClubStatusMessage && document.body.classList.contains('single-wine')) {
          statusElement.classList.add('status-clubonly-member');
          statusElement.textContent = 'Club Member Exclusive';

          if (priceWrapper) {
            priceWrapper.after(statusElement);
          }
        }
      }

      // Add custom message if available and should be shown
      if (customStatusMessage && showClubStatusMessage) {
        const customMessageElement = document.createElement('p');
        customMessageElement.classList.add('club-only-custom-message');

        try {
          // Try to decode HTML entities
          customMessageElement.innerHTML = decodeHtmlEntities(customStatusMessage);

          // Check if the content still contains encoded HTML
          if (customMessageElement.textContent.includes('&lt;') || customMessageElement.textContent.includes('&gt;')) {
            // Fallback: create a new element with the raw text
            customMessageElement.textContent = 'Club Members – ';

            // Create a login link
            const loginLink = document.createElement('a');
            loginLink.href = '/index.cfm?method=memberLogin.showLogin&referrerQueryString=';
            loginLink.textContent = 'Log In';

            customMessageElement.appendChild(loginLink);
            customMessageElement.appendChild(document.createTextNode(' to See Your Savings at Checkout'));
          }
        } catch (error) {
          console.error('Error setting HTML content:', error);
          // Fallback to plain text
          customMessageElement.textContent = 'Club Members – Log In to See Your Savings at Checkout';
        }

        statusElement.appendChild(document.createElement('br'));
        statusElement.appendChild(customMessageElement);
      }
    } else if (status === 'forsale' && customStatusMessage && showClubStatusMessage) {
      // For sale with custom message that should be shown
      statusElement.classList.add('status-forsale');

      try {
        // Try to decode HTML entities
        statusElement.innerHTML = decodeHtmlEntities(customStatusMessage);

        // Check if the content still contains encoded HTML
        if (statusElement.textContent.includes('&lt;') || statusElement.textContent.includes('&gt;')) {
          // Fallback: create a new element with the raw text
          const rawTextElement = document.createElement('p');
          rawTextElement.textContent = 'Club Members – ';

          // Create a login link
          const loginLink = document.createElement('a');
          loginLink.href = '/index.cfm?method=memberLogin.showLogin&referrerQueryString=';
          loginLink.textContent = 'Log In';

          rawTextElement.appendChild(loginLink);
          rawTextElement.appendChild(document.createTextNode(' to See Your Savings at Checkout'));

          // Clear the status element and append the raw text element
          statusElement.innerHTML = '';
          statusElement.appendChild(rawTextElement);
        }
      } catch (error) {
        console.error('Error setting HTML content:', error);
        // Fallback to plain text
        statusElement.textContent = 'Club Members – Log In to See Your Savings at Checkout';
      }

      // Find the add to cart button to place the message after it
      const addToCartButtonContainer = widget.querySelector('.v65-widgetProduct-addToCart-button');

      if (addToCartButtonContainer) {
        // Place the message after the add to cart button
        addToCartButtonContainer.after(statusElement);
      } else if (priceWrapper) {
        // Fallback: place after price wrapper if button not found
        priceWrapper.after(statusElement);
      }
    }
  },

  // Add login detection
  detectLoginStatus: function () {
    // Check if user is logged in - look for the edit profile link
    if (document.querySelector('.v65-widgetLogin-editProfile')) {
      console.log('User is logged in as a club member');
      document.body.classList.add('v65logged-in');
      document.body.classList.remove('v65logged-out');

      // Process club-only products for logged-in members
      document.querySelectorAll('.product-status-clubonly').forEach((container) => {
        // Show the add to cart button and quantity selector
        const addToCartButton = container.querySelector('.v65-widgetProduct-addToCart-button');
        const quantitySelector = container.querySelector('.v65-widgetProduct-addToCart-quantity');

        if (addToCartButton) {
          addToCartButton.style.display = 'block';
        }

        if (quantitySelector) {
          quantitySelector.style.display = 'block';
        }

        // Remove any "Club Only" messages
        const clubOnlyMessage = container.querySelector('.club-only-message');
        if (clubOnlyMessage) {
          clubOnlyMessage.remove();
        }
      });

      // Remove any logged-out content
      document.querySelectorAll('.price-logged-out').forEach((el) => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    }
    // Check if user is not logged in - look for the login link
    else if (document.querySelector('.v65-widgetLogin-login')) {
      console.log('User is not logged in');
      document.body.classList.add('v65logged-out');
      document.body.classList.remove('v65logged-in');

      // Process club-only products for non-members
      document.querySelectorAll('.product-status-clubonly').forEach((container) => {
        // Hide the add to cart button and quantity selector
        const addToCartButton = container.querySelector('.v65-widgetProduct-addToCart-button');
        const quantitySelector = container.querySelector('.v65-widgetProduct-addToCart-quantity');

        if (addToCartButton) {
          addToCartButton.style.display = 'none';
        }

        if (quantitySelector) {
          quantitySelector.style.display = 'none';
        }
      });

      // Remove any logged-in content
      document.querySelectorAll('.price-logged-in, .cart-logged-in').forEach((el) => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    }

    // Process widgets again after login status is detected
    this.processExistingWidgets();
  },
};

export default PriceDisplay;
